import { BackgroundJobStatus, BackgroundJobType } from 'types/graphql'

import { getBackgroundJobPillLabels } from 'src/fetch/backgroundJobs/backgroundJobPillLabels'
import { hasBackgroundJobPillNavigationContext } from 'src/fetch/backgroundJobs/backgroundJobPillNavigation'
import { isActiveBackgroundJobStatus } from 'src/fetch/backgroundJobs/backgroundJobStatus'

import type { TrackedBackgroundJob } from './types'

/** Shape required from pending-jobs query rows before pill tracking. */
export type PendingBackgroundJobRowForTracking = {
  id: number
  type: BackgroundJobType
  status: BackgroundJobStatus
  isInTerminalStatus?: boolean | null
  viewId?: number | null
  recordTypeId?: number | null
  processedCount: number
  totalCount: number
  successCount: number
  errorCount: number
}

/**
 * Maps a pending-jobs row to pill state. Returns null when type has no pill copy or list
 * view context are missing (e.g. bulk jobs started before the toolbar sends viewId).
 */
export const toTrackedBackgroundJob = (
  backgroundJob: PendingBackgroundJobRowForTracking
): TrackedBackgroundJob | null => {
  const pillLabels = getBackgroundJobPillLabels(backgroundJob.type)

  if (pillLabels == null || !hasBackgroundJobPillNavigationContext(backgroundJob)) {
    return null
  }

  const { viewId, recordTypeId } = backgroundJob

  return {
    id: backgroundJob.id,
    status: backgroundJob.status,
    isInTerminalStatus: backgroundJob.isInTerminalStatus ?? false,
    pillActionLabel: pillLabels.pillActionLabel,
    pillTerminalActionLabel: pillLabels.pillTerminalActionLabel,
    processedCount: backgroundJob.processedCount,
    totalCount: backgroundJob.totalCount,
    successCount: backgroundJob.successCount,
    errorCount: backgroundJob.errorCount,
    viewId,
    recordTypeId,
  }
}

/** Single-job pill: prefer the running job when multiple unacked rows exist. */
export const selectPillTrackedBackgroundJobs = (
  trackedBackgroundJobs: TrackedBackgroundJob[]
): TrackedBackgroundJob[] => {
  const firstTrackedBackgroundJob = trackedBackgroundJobs[0]
  if (!firstTrackedBackgroundJob) {
    return []
  }

  const pillBackgroundJob =
    trackedBackgroundJobs.find((trackedBackgroundJob) =>
      isActiveBackgroundJobStatus(trackedBackgroundJob.status)
    ) ?? firstTrackedBackgroundJob
  return [pillBackgroundJob]
}

export const buildTrackedJobsFromPendingRows = (
  pendingBackgroundJobs: PendingBackgroundJobRowForTracking[]
): TrackedBackgroundJob[] => {
  const trackedBackgroundJobs = pendingBackgroundJobs.flatMap((backgroundJob) => {
    const trackedBackgroundJob = toTrackedBackgroundJob(backgroundJob)
    return trackedBackgroundJob ? [trackedBackgroundJob] : []
  })
  return selectPillTrackedBackgroundJobs(trackedBackgroundJobs)
}
